import { InfiniteData } from '@tanstack/react-query';

import { queryClient } from 'services/http/queryClient';

import { PAYMENT_RECONCILIATION_DETAIL_QUERY_KEY, PAYMENT_RECONCILIATION_QUERY_KEY } from '../queryKeys';
import { GetPaymentReconciliationDetailResponse, GetPaymentReconciliationsResponse } from './paymentReconciliation';

/**
 * Force-delivering an envelope makes it ineligible on the backend straight away, but the open list is
 * an infinite query whose refetch is atomic across every loaded page: until that whole chain resolves,
 * the cached rows still carry `canForceDeliver: true`. Opening a reconciliation detail page and coming
 * back remounts the row actions and clears their in-component submit guard, so the user can submit the
 * same envelope again and get a 400. Patching the cache keeps the rows honest whenever the refetch lands.
 *
 * Only `canForceDeliver` is touched: force delivery moves the envelope to `decanonicalized`, and actual
 * delivery happens asynchronously afterwards, so writing a delivered state here would be a lie.
 */
export const markEnvelopeAsDelivered = (receiverEnvelopeId: string) => {
  queryClient.setQueriesData<InfiniteData<GetPaymentReconciliationsResponse>>(
    { queryKey: [PAYMENT_RECONCILIATION_QUERY_KEY] },
    (cached) => {
      if (!cached) {
        return cached;
      }

      return {
        ...cached,
        pages: cached.pages.map((page) => ({
          ...page,
          data: page.data.map((item) =>
            item.receiver_envelope.id === receiverEnvelopeId
              ? { ...item, receiver_envelope: { ...item.receiver_envelope, canForceDeliver: false } }
              : item,
          ),
        })),
      };
    },
  );

  queryClient.setQueriesData<GetPaymentReconciliationDetailResponse>(
    { queryKey: [PAYMENT_RECONCILIATION_DETAIL_QUERY_KEY] },
    (cached) => {
      if (!cached || cached.data.receiver_envelope.id !== receiverEnvelopeId) {
        return cached;
      }

      return {
        ...cached,
        data: { ...cached.data, receiver_envelope: { ...cached.data.receiver_envelope, canForceDeliver: false } },
      };
    },
  );
};
